<template>
  <div class="new-project h-auto p-3 pb-10">
    <nav class="flex flex-col" aria-label="Breadcrumb">
      <ol class="inline-flex items-center space-x-1 md:space-x-3">
        <li class="inline-flex items-center">
          <router-link
            to="/"
            class="inline-flex items-center text-sm font-medium text-zinc-700 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
          >
            <svg
              class="mr-2 w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
              ></path>
            </svg>
            Home
          </router-link>
        </li>
        <li>
          <div class="flex items-center">
            <svg
              class="w-6 h-6 text-zinc-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <router-link
              to="/portfolio/past-projects"
              class="ml-1 text-sm font-medium text-zinc-700 hover:text-zinc-900 md:ml-2 dark:text-zinc-400 dark:hover:text-white"
              >Portfolio | New Project</router-link
            >
          </div>
        </li>
      </ol>

      <div class="mt-5 w-full">
        <h1 class="text-2xl text-zinc-900 font-medium dark:text-zinc-200">
          New Project
        </h1>
        <p class="mt-1 text-sm font-normal text-zinc-400">
          Add your new project based on language type, project type, etc.
        </p>
      </div>
    </nav>

    <div
      class="bg-white dark:bg-zinc-800 p-5 w-full box-border border dark:border-zinc-700 mt-5"
    >
      <div class="flex justify-start">
        <div class="flex flex-col gap-2">
          <h6 class="font-medium text-sm text-zinc-600 dark:text-zinc-400">
            Project Language
          </h6>
          <div
            class="border-2 bg-zinc-100 dark:bg-zinc-900 dark:border-zinc-700 w-full box-border flex"
          >
            <dropdown placement="left" class="w-full">
              <template v-slot:button>
                <button
                  class="flex justify-between w-full px-4 py-3 bg-zinc-100 dark:bg-zinc-900 text-sm dark:border-zinc-700"
                >
                  <span class="text-zinc-500 dark:text-zinc-400">{{
                    currentLang
                  }}</span>

                  <span class="ml-4 mt-1 text-zinc-500"
                    ><Icon icon="ant-design:caret-down-filled"
                  /></span>
                </button>
              </template>
              <template v-slot:content>
                <div class="uppercase text-xs">
                  <button
                    @click="changeLang('En')"
                    type="button"
                    class="block hover:bg-jestaBlue hover:text-white px-4 py-2 w-full text-left"
                  >
                    En
                  </button>
                  <button
                    type="button"
                    @click="changeLang('Fr')"
                    class="block hover:bg-jestaBlue hover:text-white px-4 py-2 w-full text-left"
                  >
                    Fr
                  </button>
                </div>
              </template>
            </dropdown>
          </div>
        </div>
      </div>

      <form class="mt-3" @submit.prevent="save">
        <div class="informations flex flex-col gap-4 mb-5">
          <Input
            type="text"
            placeholder="Project Title"
            v-model="addProject.title"
            :isRequired="true"
          ></Input>

          <div class="flex max-md:flex-col gap-x-4 gap-y-5">
            <FilterBox id="countrySelect" title="Project Country">
              <template v-slot:title>
                <span>Country</span>
              </template>
              <template v-slot:body>
                <div
                  v-for="(item, index) in allCountries"
                  :key="item.id"
                  class="flex justify-between items-center text-black dark:text-white"
                  :class="
                    index !== allCountries.length - 1
                      ? 'pb-3 mb-3 border-b-2 dark:border-zinc-700'
                      : ''
                  "
                >
                  <div class="flex items-center gap-3">
                    <input
                      v-model="addProject.countryId"
                      class="relative float-left h-5 w-5 appearance-none rounded-full border-2 border-solid border-zinc-400 before:pointer-events-none before:absolute before:h-4 before:w-4 before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_8px_transparent] before:content-[''] after:absolute after:z-[1] after:block after:h-4 after:w-4 after:rounded-full after:content-[''] checked:border-jestaBlue checked:before:opacity-[0.16] checked:after:absolute checked:after:left-1/2 checked:after:top-1/2 checked:after:h-[0.625rem] checked:after:w-[0.625rem] checked:after:rounded-full checked:after:border-jestaBlue checked:after:bg-jestaBlue checked:after:content-[''] checked:after:[transform:translate(-50%,-50%)] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_8px_rgba(0,0,0,0.6)] focus:shadow-none focus:outline-none focus:ring-0 focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_8px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:border-jestaBlue checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_8px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] dark:border-zinc-500 dark:checked:border-jestaBlue dark:checked:after:border-jestaBlue dark:checked:after:bg-jestaBlue dark:focus:before:shadow-[0px_0px_0px_8px_rgba(255,255,255,0.4)] dark:checked:focus:border-jestaBlue dark:checked:focus:before:shadow-[0px_0px_0px_8px_#3b71ca]"
                      type="radio"
                      :value="item.id"
                      name="countryRadio"
                    />

                    <input
                      type="text"
                      v-model="item.name"
                      :disabled="!item.editableFilterItems"
                      class="text-black text-sm dark:text-white bg-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 dark:bg-zinc-900 outline-none"
                    />
                  </div>

                  <div class="flex relative h-full items-center">
                    <div
                      :class="
                        !item.editableFilterItems
                          ? 'flex items-center gap-2'
                          : 'hidden'
                      "
                    >
                      <button
                        class="text-zinc-600 dark:text-zinc-400 text-lg"
                        @click="() => deleteItem(item.id, 'country')"
                        type="button"
                      >
                        <Icon icon="tabler:trash-filled" />
                      </button>
                      <button
                        @click="() => editableItems(item)"
                        type="button"
                        class="text-zinc-600 dark:text-zinc-400 text-lg"
                      >
                        <Icon icon="ri:ball-pen-fill" />
                      </button>
                    </div>

                    <!-- Confirm change buttons -->
                    <div
                      :class="
                        item.editableFilterItems
                          ? 'flex items-center gap-2'
                          : 'hidden'
                      "
                    >
                      <button
                        @click="() => (item.editableFilterItems = false)"
                        class="text-zinc-600 dark:text-zinc-400 text-lg"
                        type="button"
                      >
                        <Icon icon="heroicons:x-mark-20-solid" />
                      </button>
                      <button
                        @click="() => editItem(item, 'country')"
                        type="button"
                        class="text-zinc-600 dark:text-zinc-400 text-lg"
                      >
                        <Icon icon="material-symbols:check" />
                      </button>
                    </div>
                  </div>
                </div>

                <div class="relative flex mt-6">
                  <input
                    type="text"
                    placeholder="Add New Country"
                    v-model="this.newCountryValue"
                    class="p-2 w-full text-sm text-black dark:text-white border-zinc-300 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 border-2 bg-zinc-100 dark:bg-zinc-900 dark:border-zinc-700 outline-none"
                  />
                  <button
                    @click="() => addItem(this.newCountryValue, 'country')"
                    class="border-2 border-l-0 p-2 border-zinc-300 dark:border-zinc-600 text-xl text-zinc-600 dark:text-zinc-400"
                    data-te-ripple-init
                    type="button"
                    id="button-addon1"
                  >
                    <Icon icon="ic:baseline-plus" />
                  </button>
                </div>
              </template>
            </FilterBox>

            <FilterBox id="typeSelect" title="Project Types">
              <template v-slot:title>
                <span>Types</span>
              </template>
              <template v-slot:body>
                <div
                  v-for="(item, index) in allProjectTypes"
                  :key="item.id"
                  class="flex justify-between items-center text-black dark:text-white"
                  :class="
                    index !== allProjectTypes.length - 1
                      ? 'pb-3 mb-3 border-b-2 dark:border-zinc-700'
                      : ''
                  "
                >
                  <div class="flex items-center gap-3">
                    <input
                      v-model="addProject.typeIds"
                      :value="item.id"
                      class="relative h-[1.125rem] w-[1.125rem] appearance-none rounded-[0.25rem] border-[0.125rem] border-solid border-zinc-400 outline-none before:pointer-events-none before:absolute before:h-[0.875rem] before:w-[0.875rem] before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_8px_transparent] before:content-[''] checked:border-jestaBlue checked:bg-jestaBlue checked:before:opacity-[0.16] checked:after:absolute checked:after:-mt-px checked:after:ml-[0.25rem] checked:after:block checked:after:h-[0.8125rem] checked:after:w-[0.375rem] checked:after:rotate-45 checked:after:border-[0.125rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[''] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_8px_rgba(0,0,0,0.6)] focus:shadow-none focus:transition-[border-color_0.2s] focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_8px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] focus:after:absolute focus:after:z-[1] focus:after:block focus:after:h-[0.875rem] focus:after:w-[0.875rem] focus:after:rounded-[0.125rem] focus:after:content-[''] checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_8px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:after:-mt-px checked:focus:after:ml-[0.25rem] checked:focus:after:h-[0.8125rem] checked:focus:after:w-[0.375rem] checked:focus:after:rotate-45 checked:focus:after:rounded-none checked:focus:after:border-[0.125rem] checked:focus:after:border-l-0 checked:focus:after:border-t-0 checked:focus:after:border-solid checked:focus:after:border-white checked:focus:after:bg-transparent dark:border-zinc-500 dark:checked:border-jestaBlue dark:checked:bg-jestaBlue dark:focus:before:shadow-[0px_0px_0px_8px_rgba(255,255,255,0.4)] dark:checked:focus:before:shadow-[0px_0px_0px_8px_#3b71ca]"
                      type="checkbox"
                    />

                    <input
                      type="text"
                      v-model="item.type"
                      :disabled="!item.editableFilterItems"
                      class="text-black text-sm dark:text-white bg-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 dark:bg-zinc-900 outline-none"
                    />
                  </div>
                  <div class="flex relative h-full items-center">
                    <div
                      :class="
                        !item.editableFilterItems
                          ? 'flex items-center gap-2'
                          : 'hidden'
                      "
                    >
                      <button
                        @click="deleteItem(item.id, 'type')"
                        class="text-zinc-600 dark:text-zinc-400 text-lg"
                        type="button"
                      >
                        <Icon icon="tabler:trash-filled" />
                      </button>
                      <button
                        @click="editableItems(item)"
                        type="button"
                        class="text-zinc-600 dark:text-zinc-400 text-lg"
                      >
                        <Icon icon="ri:ball-pen-fill" />
                      </button>
                    </div>

                    <!-- Confirm change buttons -->
                    <div
                      :class="
                        item.editableFilterItems
                          ? 'flex items-center gap-2'
                          : 'hidden'
                      "
                    >
                      <button
                        @click="() => (item.editableFilterItems = false)"
                        class="text-zinc-600 dark:text-zinc-400 text-lg"
                        type="button"
                      >
                        <Icon icon="heroicons:x-mark-20-solid" />
                      </button>

                      <button
                        @click="editItem(item, 'type')"
                        type="button"
                        class="text-zinc-600 dark:text-zinc-400 text-lg"
                      >
                        <Icon icon="material-symbols:check" />
                      </button>
                    </div>
                  </div>
                </div>

                <div class="relative flex mt-6">
                  <input
                    type="text"
                    placeholder="Add New Type"
                    v-model="this.newTypeValue"
                    class="p-2 w-full text-sm text-black dark:text-white border-zinc-300 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 border-2 bg-zinc-100 dark:bg-zinc-900 dark:border-zinc-700 outline-none"
                  />
                  <button
                    @click="addItem(this.newTypeValue, 'type')"
                    class="border-2 border-l-0 p-2 border-zinc-300 dark:border-zinc-600 text-xl text-zinc-600 dark:text-zinc-400"
                    data-te-ripple-init
                    type="button"
                    id="button-addon1"
                  >
                    <Icon icon="ic:baseline-plus" />
                  </button>
                </div>
              </template>
            </FilterBox>

            <Input
              type="text"
              placeholder="City"
              v-model="addProject.city"
              :isRequired="true"
            ></Input>
          </div>
          <div class="flex max-md:flex-col gap-x-4 gap-y-5">
            <div class="flex flex-col gap-2 w-full">
              <h6 class="font-medium text-sm text-zinc-500 dark:text-zinc-400">
                Project First Image
              </h6>
              <FileUploader
                @updatePendingFiles="updatePendingFiles($event, 'firstImage')"
              ></FileUploader>
            </div>
            <div class="flex flex-col gap-2 w-full">
              <h6 class="font-medium text-sm text-zinc-500 dark:text-zinc-400">
                Project Second Image
              </h6>
              <FileUploader
                @updatePendingFiles="updatePendingFiles($event, 'secondImage')"
              ></FileUploader>
            </div>
          </div>
          <Input
            type="textarea"
            placeholder="Description"
            v-model="addProject.body"
            :isRequired="true"
          ></Input>

          <div class="flex flex-col justify-center items-start gap-3">
            <Input
              type="number"
              placeholder="Ordering"
              v-model="addProject.ordering"
            ></Input>

            <span class="italic text-sm text-black dark:text-white"
              >* Notice: The default ordering value is 10,000 so that you can
              create a different order between each project.</span
            >
          </div>

          <div class="flex items-center gap-1 mt-3">
            <input
              class="relative h-[1.125rem] w-[1.125rem] appearance-none rounded-[0.25rem] border-[0.125rem] border-solid border-zinc-400 outline-none before:pointer-events-none before:absolute before:h-[0.875rem] before:w-[0.875rem] before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_8px_transparent] before:content-[''] checked:border-jestaBlue checked:bg-jestaBlue checked:before:opacity-[0.16] checked:after:absolute checked:after:-mt-px checked:after:ml-[0.25rem] checked:after:block checked:after:h-[0.8125rem] checked:after:w-[0.375rem] checked:after:rotate-45 checked:after:border-[0.125rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[''] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_8px_rgba(0,0,0,0.6)] focus:shadow-none focus:transition-[border-color_0.2s] focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_8px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] focus:after:absolute focus:after:z-[1] focus:after:block focus:after:h-[0.875rem] focus:after:w-[0.875rem] focus:after:rounded-[0.125rem] focus:after:content-[''] checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_8px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:after:-mt-px checked:focus:after:ml-[0.25rem] checked:focus:after:h-[0.8125rem] checked:focus:after:w-[0.375rem] checked:focus:after:rotate-45 checked:focus:after:rounded-none checked:focus:after:border-[0.125rem] checked:focus:after:border-l-0 checked:focus:after:border-t-0 checked:focus:after:border-solid checked:focus:after:border-white checked:focus:after:bg-transparent dark:border-zinc-500 dark:checked:border-jestaBlue dark:checked:bg-jestaBlue dark:focus:before:shadow-[0px_0px_0px_8px_rgba(255,255,255,0.4)] dark:checked:focus:before:shadow-[0px_0px_0px_8px_#3b71ca]"
              type="checkbox"
              id="isFormerCheckBox"
              v-model="addProject.IsFormerTransaction"
            />

            <label
              class="text-sm text-black dark:text-white"
              for="isFormerCheckBox"
              >Is Former Transaction?</label
            >
          </div>
        </div>
        <hr class="w-full border dark:border-zinc-700" />
        <div class="wrapper-button flex justify-end mt-5">
          <button
            v-if="!isAdding"
            type="submit"
            class="text-white bg-jestaBlue hover:bg-jestaBlue/80 transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
          >
            Save
          </button>
          <button
            v-else
            disabled
            type="button"
            class="text-white bg-jestaBlue opacity-60 cursor-default transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
          >
            <svg
              role="status"
              class="inline w-4 h-4 mr-1 text-white animate-spin"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="#E5E7EB"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
            Uploading...
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import { Icon } from "@iconify/vue";

import Input from "@/components/Input.vue";
import FileUploader from "@/components/FileUploader.vue";
import FilterBox from "@/components/FilterBox.vue";

import axios from "axios";

import Dropdown from "@/components/Dropdown.vue";

export default {
  name: "Dashboard",
  data() {
    return {
      images: {
        firstImage: "",
        secondImage: "",
      },
      addProject: {
        title: "",
        body: "",
        city: "",
        IsFormerTransaction: false,
        countryId: null,
        typeIds: [],
        webSite: null,
        ordering: 10000,
      },
      allCountries: [],
      allProjectTypes: [],
      newCountryValue: "",
      newTypeValue: "",
      isAdding: false,
      percentCompleted: 50,
      currentLang: "En",
    };
  },
  components: {
    Icon,
    FileUploader,
    FilterBox,
    Input,
    Dropdown,
  },
  async created() {
    await this.getAllCountries();
    await this.getAllProjectTypes();
  },
  mounted() {},
  methods: {
    async getAllCountries() {
      await axios
        .get("Portfolios/GetAllCountries")
        .then((response) => {
          this.allCountries = response.data?.value;
        })
        .catch((error) => {
          console.log(error);
          console.log(error.response);
        });
    },
    async getAllProjectTypes() {
      this.allProjectTypes = [];

      await axios
        .get(`Portfolios/GetAllTypes?lang=${this.currentLang}`)
        .then((response) => {
          this.allProjectTypes = response.data?.value;
        })
        .catch((error) => {
          console.log(error);
          console.log(error.response);
        });
    },
    updatePendingFiles: function (files, imageType, sliderIndex = 0) {
      if (files) {
        if (imageType == "firstImage") {
          this.images.firstImage = files[0]?.file;
        }
        if (imageType == "secondImage") {
          this.images.secondImage = files[0]?.file;
        }
      }
    },
    editableItems(item) {
      item.editableFilterItems = !item.editableFilterItems;
    },
    async save() {
      var bodyFormData = new FormData();
      bodyFormData.append("Lang", this.currentLang);
      bodyFormData.append("Title", this.addProject.title);
      bodyFormData.append("Body", this.addProject.body);
      bodyFormData.append("City", this.addProject.city);

      if (this.addProject.ordering) {
        bodyFormData.append("Ordering", this.addProject.ordering);
      }

      bodyFormData.append(
        "IsFormerTransaction",
        this.addProject.IsFormerTransaction
      );

      if (!this.addProject.countryId) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Country Is Required!",
          body: "You must choice one country!",
        });
        return;
      }

      if (!this.addProject.typeIds || this.addProject.typeIds.length == 0) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Project Type Is Required!",
          body: "You must choice one project type at least!",
        });
        return;
      }

      bodyFormData.append("CountryId", this.addProject.countryId);

      this.addProject.typeIds.forEach((current) =>
        bodyFormData.append("Types", current)
      );

      if (!this.images.firstImage) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Project First Image Is Required!",
          body: "You must choice image for project first image!",
        });
        return;
      }

      bodyFormData.append(
        "PictureOne",
        this.images.firstImage,
        this.images.firstImage.name
      );

      if (this.images.secondImage) {
        bodyFormData.append(
          "PictureTwo",
          this.images.secondImage,
          this.images.secondImage.name
        );
      }

      let options = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        isNeedJwtAuth: true,
        // onUploadProgress: function (progressEvent) {

        //   this.percentCompleted = Math.round(
        //     (progressEvent.loaded * 100) / progressEvent.total
        //   );
        // },
      };

      this.isAdding = true;

      await axios
        .post("portfolios", bodyFormData, options)
        .then((response) => {
          window.scrollTo(0, 0);
          this.$store.commit("showToast", {
            id: "myToast",
            type: "success",
            title: "Add Successful!",
            body: "The adition was successful!",
          });
          this.isAdding = false;
        })
        .catch((error) => {
          console.log(error);
          console.log(error.response);
          if (error.response?.data?.isSuccess == false) {
            this.$store.commit("showToast", {
              id: "myToast",
              type: "error",
              title: "Add Failed!",
              body: error.response?.data?.messages[0],
            });
          }
          this.isAdding = false;
        });
    },
    async deleteItem(id, type) {
      if (type == "country") {
        await this.deleteCountry(id);
      }

      if (type == "type") {
        await this.deleteType(id);

        await this.getAllProjectTypes();
      }
    },
    async editItem(item, type) {
      if (type == "country") {
        await this.updateCountry(item);
      }

      if (type == "type") {
        await this.updateType(item);
      }
    },
    async addItem(newValue, type) {
      if (type == "country") {
        await this.addCountry(newValue);
      }

      if (type == "type") {
        await this.addType(newValue);
      }
    },

    async deleteCountry(id) {
      await axios
        .delete(`Portfolios/DeleteCountry/${id}`, { isNeedJwtAuth: true })
        .then(async (response) => {
          await this.getAllCountries();
        })
        .catch((error) => {
          if (!error.response?.data?.isSuccess) {
            console.log(error.response?.data?.messages[0]);
            return;
          }
          console.log(error);
          console.log(error.response);
        });
    },
    async updateCountry(item) {
      await axios
        .put(
          `Portfolios/UpdateCountry`,
          { id: item.id, name: item.name },
          { isNeedJwtAuth: true }
        )
        .then(async (response) => {
          item.editableFilterItems = false;
        })
        .catch((error) => {
          if (!error.response?.data?.isSuccess) {
            console.log(error.response?.data?.messages[0]);
            return;
          }
          console.log(error);
          console.log(error.response);
        });
    },
    async addCountry(newValue) {
      await axios
        .post("portfolios/AddCountry", { name: newValue })
        .then(async (response) => {
          await this.getAllCountries();
        })
        .catch((error) => {
          console.log(error);
          console.log(error.response);
        });
    },

    async deleteType(id) {
      await axios
        .delete(`Portfolios/DeleteType/${id}`, { isNeedJwtAuth: true })
        .then(async (response) => {
          await this.getAllProjectTypes();
        })
        .catch((error) => {
          if (!error.response?.data?.isSuccess) {
            console.log(error.response?.data?.messages[0]);
            return;
          }
          console.log(error);
          console.log(error.response);
        });
    },
    async updateType(item) {
      await axios
        .put(
          `Portfolios/UpdateType`,
          { id: item.id, type: item.type },
          { isNeedJwtAuth: true }
        )
        .then(async (response) => {
          item.editableFilterItems = false;
        })
        .catch((error) => {
          if (!error.response?.data?.isSuccess) {
            console.log(error.response?.data?.messages[0]);
            return;
          }
          console.log(error);
          console.log(error.response);
        });
    },
    async addType(newValue) {
      await axios
        .post("portfolios/AddType", { type: newValue, lang: this.currentLang })
        .then(async (response) => {
          await this.getAllProjectTypes();
        })
        .catch((error) => {
          console.log(error);
          console.log(error.response);
        });
    },
    async changeLang(lang) {
      this.currentLang = lang;
      await this.getAllProjectTypes();
    },
  },
};
</script>
